import { z } from 'zod';
import { objectIdSchema } from './objectId';
import { AudienceSchema } from './audience';
import { WithTimestampSchema, WithIdSchema } from './utils';
import { BareStaffAllocationSchema } from './staffAllocation';
import { McmSchema } from './mcm';
import { BrandSegmentSchema } from './brandSegment';
import { BrandLevelSchema } from './brandLevel';
import { BrandAttributeSchema } from './brandAttribute';

export const EventDisplayOption = [
  'show-as-24/7',
  'show-finish-time',
  'do-not-show-finish-time',
  'ends-late',
  'override-start-time',
] as const;

export const DefaultEventDisplayOptionSchema = z.literal('show-finish-time');

export const StrategicLibraryTransfer = ['promote-to-brand', 'add-property-to-access'] as const;

export const BareEventPropertyInfoSchema = z.object({
  _id: objectIdSchema.optional(),
  property: objectIdSchema,
  defaultStaffAllocations: z.array(BareStaffAllocationSchema),
});

export const NewEventTemplateSchema = z.object({
  title: z.string(),
  description: z.string(),
  segment: objectIdSchema.optional(),
  levels: z.array(objectIdSchema),
  attributes: z.array(objectIdSchema),
  incomplete: z.boolean(),
  defaultDuration: z.number(), // In minutes
  brand: objectIdSchema,
  properties: z.array(BareEventPropertyInfoSchema),
  source: objectIdSchema.optional(),
  mcm: McmSchema.optional(),
  audience: AudienceSchema.optional(),
  images: z.array(z.string()),
  softConflict: z.boolean(),
  isPrivate: z.boolean(),
  crewOnly: z.boolean().optional(),
  notNative: z.boolean().optional(),
  interactiveFeedDisplay: z.enum(EventDisplayOption),
  archived: z.boolean().optional(),

  // VV fields
  inventoried: z.boolean().optional(),
  initialInventory: z.number().optional(),
  priced: z.boolean().optional(),
  initialPrice: z.number().optional(),
  vip: z.boolean().optional(),
  simple: z.boolean().optional(),
  homecoming: z.boolean().optional(),
  allAboard: z.boolean().optional(),
  shortCode: z.string().optional(), // Used for packageTypes at VV
});

export const BareEventTemplateSchema = NewEventTemplateSchema.and(WithIdSchema).and(
  WithTimestampSchema.partial()
);

export const EventTemplateSchema = z.object({
  _id: objectIdSchema,
  title: z.string(),
  description: z.string(),
  segment: z.optional(BrandSegmentSchema).nullable(),
  levels: z.array(BrandLevelSchema),
  attributes: z.array(BrandAttributeSchema),
  incomplete: z.boolean(),
  defaultDuration: z.number().nullable(), // In minutes
  brand: objectIdSchema,
  properties: BareEventPropertyInfoSchema.array(),
  source: z.union([objectIdSchema, z.string()]).optional().nullable(),
  mcm: McmSchema.optional(),
  softConflict: z.boolean(),
  isPrivate: z.boolean(),
  crewOnly: z.boolean().optional(),
  notNative: z.boolean().optional(),
  interactiveFeedDisplay: z.union([z.enum(EventDisplayOption), z.string()]).nullable(),
  archived: z.boolean().optional(),

  // VV fields
  inventoried: z.boolean().optional(),
  initialInventory: z.number().optional().nullable(),
  priced: z.boolean().optional(),
  initialPrice: z.number().optional(),
  vip: z.boolean().optional(),
  simple: z.boolean().optional(),
  homecoming: z.boolean().optional(),
  allAboard: z.boolean().optional(),

  createdAt: z.date().optional(),
  updatedAt: z.date().optional(),
});

export const EvaluatedEventTemplateSchema = EventTemplateSchema.merge(
  z.object({
    _evaluatedTitle: z.string().optional(),
    _evaluatedDesc: z.string().optional(),
  })
);

export type EventTemplate = z.infer<typeof EventTemplateSchema>;
